/**
 * Mock Data Management System
 * Provides static mock responses for API simulation during development
 */

export interface ColumnMetadata {
  name: string;
  dataType: 'STRING' | 'INTEGER' | 'DECIMAL' | 'DATE' | 'BOOLEAN';
  nullable: boolean;
  availableOptions?: string[]; // For categorical data
  statistics: {
    distinctCount: number;
    nullCount: number;
    min?: number; // For numeric data
    max?: number; // For numeric data
    mean?: number; // For numeric data
  };
  suggested?: string[]; // Suggested target containers
}

export interface SmartSuggestions {
  baseMetrics: string[]; // All numeric columns
  volumeMetrics: string[]; // Numeric columns with "units", "volume", "qty" in name
  mixDimensions: string[]; // String columns with 2-50 distinct values
  scenarioField: string[]; // Columns named "scenario", case-insensitive
  periodField: string[]; // Columns named "period", "date", "time", case-insensitive
  rateMetrics?: string[]; // Existing rate columns
}

export interface MockFileAnalysisResponse {
  rowCount: number;
  columns: ColumnMetadata[];
  smartSuggestions: SmartSuggestions;
  fileMetadata: {
    fileName: string;
    fileSize: number;
    uploadedAt: string;
  };
}

export interface MockResponse {
  data: any;
  metadata: {
    timestamp: string;
    mock: boolean;
  };
}

/**
 * Mock Data Manager
 * Manages static mock responses and simulates API behavior
 */
export class MockDataManager {
  private static mockResponses: Map<string, any> = new Map();
  
  /**
   * Register a mock response for a given key
   */
  static registerMockResponse(key: string, response: any): void {
    this.mockResponses.set(key, response);
  }
  
  /**
   * Simulate an API call with delay and return mock response
   */
  static async simulateAPICall(
    mockKey: string,
    delay: number = 1500
  ): Promise<MockResponse> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, delay));
    
    const mockResponse = this.mockResponses.get(mockKey);
    if (!mockResponse) {
      throw new Error(`Mock response not found for key: ${mockKey}`);
    }
    
    return {
      data: mockResponse,
      metadata: { 
        timestamp: new Date().toISOString(),
        mock: true 
      }
    };
  }
  
  /**
   * Get available mock response keys
   */
  static getAvailableKeys(): string[] {
    return Array.from(this.mockResponses.keys());
  }
  
  /**
   * Clear all mock responses
   */
  static clearAllMockResponses(): void {
    this.mockResponses.clear();
  }
  
  /**
   * Check if mock key exists
   */
  static hasMockResponse(key: string): boolean {
    return this.mockResponses.has(key);
  }
}

// Register RVM column analysis mock data (based on documentation example)
MockDataManager.registerMockResponse('rvm_column_analysis_mock', {
  rowCount: 8,
  columns: [
    {
      name: "FC",
      dataType: "STRING",
      nullable: false,
      availableOptions: ["Sort", "NonSort"],
      statistics: { distinctCount: 2, nullCount: 0 },
      suggested: ["mixDimensions"]
    },
    {
      name: "Size", 
      dataType: "STRING",
      nullable: false,
      availableOptions: ["Large", "Small"],
      statistics: { distinctCount: 2, nullCount: 0 },
      suggested: ["mixDimensions"]
    },
    {
      name: "Cost",
      dataType: "INTEGER",
      nullable: false,
      statistics: { distinctCount: 6, nullCount: 0, min: 1200, max: 3400, mean: 2200 },
      suggested: ["baseMetrics"]
    },
    {
      name: "Hours",
      dataType: "INTEGER", 
      nullable: false,
      statistics: { distinctCount: 6, nullCount: 0, min: 60, max: 165, mean: 110 },
      suggested: ["baseMetrics"]
    },
    {
      name: "Throughput",
      dataType: "INTEGER",
      nullable: false,
      statistics: { distinctCount: 6, nullCount: 0, min: 1500, max: 3300, mean: 2200 },
      suggested: ["baseMetrics"]
    },
    {
      name: "Units",
      dataType: "INTEGER",
      nullable: false,
      statistics: { distinctCount: 7, nullCount: 0, min: 500, max: 3000, mean: 1500 },
      suggested: ["volumeMetrics", "baseMetrics"]
    },
    {
      name: "Scenario",
      dataType: "STRING",
      nullable: false,
      availableOptions: ["Actuals"],
      statistics: { distinctCount: 1, nullCount: 0 },
      suggested: ["scenarioField"]
    },
    {
      name: "Period",
      dataType: "STRING",
      nullable: false,
      availableOptions: ["202411", "202412"],
      statistics: { distinctCount: 2, nullCount: 0 },
      suggested: ["periodField"]
    }
  ],
  smartSuggestions: {
    baseMetrics: ["Cost", "Hours", "Throughput", "Units"],
    volumeMetrics: ["Units"],
    mixDimensions: ["FC", "Size"],
    scenarioField: ["Scenario"],
    periodField: ["Period"],
    rateMetrics: [] // No existing rate columns in this example
  },
  fileMetadata: {
    fileName: "mock_data.csv",
    fileSize: 1024,
    uploadedAt: new Date().toISOString()
  }
} as MockFileAnalysisResponse);

// Register additional mock responses for development scenarios
MockDataManager.registerMockResponse('rvm_analysis_error_mock', {
  error: "Failed to analyze file: Invalid file format. Please upload a CSV file with proper column headers."
});

MockDataManager.registerMockResponse('rvm_large_dataset_mock', {
  rowCount: 50000,
  columns: [
    {
      name: "Region",
      dataType: "STRING",
      nullable: false,
      availableOptions: ["North", "South", "East", "West", "Central"],
      statistics: { distinctCount: 5, nullCount: 0 },
      suggested: ["mixDimensions"]
    },
    {
      name: "Product_Line",
      dataType: "STRING", 
      nullable: false,
      availableOptions: ["Electronics", "Clothing", "Books", "Home"],
      statistics: { distinctCount: 4, nullCount: 0 },
      suggested: ["mixDimensions"]
    },
    {
      name: "Revenue",
      dataType: "DECIMAL",
      nullable: false,
      statistics: { distinctCount: 45000, nullCount: 0, min: 100.00, max: 50000.00, mean: 5500.50 },
      suggested: ["baseMetrics"]
    },
    {
      name: "COGS",
      dataType: "DECIMAL",
      nullable: false,
      statistics: { distinctCount: 43000, nullCount: 0, min: 50.00, max: 30000.00, mean: 3200.25 },
      suggested: ["baseMetrics"]
    },
    {
      name: "Volume",
      dataType: "INTEGER",
      nullable: false,
      statistics: { distinctCount: 5000, nullCount: 0, min: 1, max: 10000, mean: 850 },
      suggested: ["volumeMetrics"]
    },
    {
      name: "CPU", // Existing rate metric
      dataType: "DECIMAL",
      nullable: false,
      statistics: { distinctCount: 12000, nullCount: 0, min: 0.01, max: 45.50, mean: 6.50 },
      suggested: ["rateMetrics"]
    },
    {
      name: "Scenario_Type",
      dataType: "STRING",
      nullable: false,
      availableOptions: ["Budget", "Actual", "Forecast"],
      statistics: { distinctCount: 3, nullCount: 0 },
      suggested: ["scenarioField"]
    },
    {
      name: "Quarter",
      dataType: "STRING",
      nullable: false,
      availableOptions: ["Q1-2025", "Q2-2025", "Q3-2025", "Q4-2025"],
      statistics: { distinctCount: 4, nullCount: 0 },
      suggested: ["periodField"]
    }
  ],
  smartSuggestions: {
    baseMetrics: ["Revenue", "COGS"],
    volumeMetrics: ["Volume"],
    mixDimensions: ["Region", "Product_Line"],
    scenarioField: ["Scenario_Type"],
    periodField: ["Quarter"],
    rateMetrics: ["CPU"]
  },
  fileMetadata: {
    fileName: "large_dataset.csv",
    fileSize: 52428800, // 50MB
    uploadedAt: new Date().toISOString()
  }
} as MockFileAnalysisResponse);
